import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import OfflineBanner from './components/layout/OfflineBanner';
import MovedBanner from './components/layout/MovedBanner';
import UpdatePrompt from './components/layout/UpdatePrompt';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingSpinner from './components/common/LoadingSpinner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NetworkProvider } from './context/NetworkContext';
import { SyncProvider } from './context/SyncContext';
import { ToastProvider } from './context/ToastContext';
import lazyPage from './utils/lazyPage';

// Pages load on demand; lazyPage reloads once if a deploy replaced the bundles.
const Dashboard = lazyPage(() => import('./pages/Dashboard'));
const MapPage = lazyPage(() => import('./pages/MapPage'));
const Login = lazyPage(() => import('./pages/auth/Login'));
const ChooseNewPassword = lazyPage(() => import('./pages/auth/ChooseNewPassword'));
const SurveyList = lazyPage(() => import('./pages/surveys/SurveyList'));
const SurveyForm = lazyPage(() => import('./pages/surveys/SurveyForm'));
const SurveyDetail = lazyPage(() => import('./pages/surveys/SurveyDetail'));
const TransectForm = lazyPage(() => import('./pages/transects/TransectForm'));
const TransectDetail = lazyPage(() => import('./pages/transects/TransectDetail'));
const QuadratForm = lazyPage(() => import('./pages/quadrats/QuadratForm'));
const QuadratDetail = lazyPage(() => import('./pages/quadrats/QuadratDetail'));
const MatureTreeForm = lazyPage(() => import('./pages/mature-trees/MatureTreeForm'));
const RegenerationPlotForm = lazyPage(() => import('./pages/regeneration-plots/RegenerationPlotForm'));
const DisturbanceForm = lazyPage(() => import('./pages/disturbances/DisturbanceForm'));
const UsersPage = lazyPage(() => import('./pages/admin/UsersPage'));
const SpeciesPage = lazyPage(() => import('./pages/species/SpeciesPage'));
const UserGuide = lazyPage(() => import('./pages/help/UserGuide'));
const NotFound = lazyPage(() => import('./pages/NotFound'));

// Requires a signed-in user, and optionally a permission or a role.
const ProtectedRoute = ({ children, permission, role }) => {
  const { user, hasPermission, hasRole } = useAuth();
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if ((permission && !hasPermission(permission)) || (role && !hasRole(role))) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Signed-in users have no reason to see the auth pages.
const PublicOnlyRoute = ({ children }) => {
  const { user } = useAuth();
  return user ? <Navigate to="/" replace /> : children;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
};

const AppContent = () => {
  const { user } = useAuth();
  const location = useLocation();

  // A new account (or one an admin reset) must choose its own password before
  // anything else; the server enforces the same rule.
  if (user?.must_change_password) {
    return (
      <Suspense fallback={<LoadingSpinner fullScreen />}>
        <ChooseNewPassword />
        <UpdatePrompt />
      </Suspense>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <ScrollToTop />
      {user && <Navbar />}
      {user && <MovedBanner />}
      {user && <OfflineBanner />}
      <Box component="main" className="page-enter" key={location.pathname} sx={{ flexGrow: 1 }}>
        <ErrorBoundary resetKey={location.pathname}>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Auth */}
              <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
              {/* Accounts are created by admins; old sign-up links land on the sign-in page. */}
              <Route path="/register" element={<Navigate to="/login" replace />} />

              {/* Overview */}
              <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/map" element={<ProtectedRoute><MapPage /></ProtectedRoute>} />

              {/* Surveys */}
              <Route path="/surveys" element={<ProtectedRoute permission="view_survey"><SurveyList /></ProtectedRoute>} />
              <Route path="/surveys/new" element={<ProtectedRoute permission="create_survey"><SurveyForm /></ProtectedRoute>} />
              <Route path="/surveys/:surveyId" element={<ProtectedRoute permission="view_survey"><SurveyDetail /></ProtectedRoute>} />
              <Route path="/surveys/:surveyId/edit" element={<ProtectedRoute permission="edit_survey"><SurveyForm /></ProtectedRoute>} />

              {/* Transects */}
              <Route path="/surveys/:surveyId/transects/new" element={<ProtectedRoute permission="create_transect"><TransectForm /></ProtectedRoute>} />
              <Route path="/transects/:transectId" element={<ProtectedRoute permission="view_transect"><TransectDetail /></ProtectedRoute>} />
              <Route path="/transects/:transectId/edit" element={<ProtectedRoute permission="edit_transect"><TransectForm /></ProtectedRoute>} />

              {/* Quadrats */}
              <Route path="/transects/:transectId/quadrats/new" element={<ProtectedRoute permission="create_survey"><QuadratForm /></ProtectedRoute>} />
              <Route path="/quadrats/:quadratId" element={<ProtectedRoute permission="view_survey"><QuadratDetail /></ProtectedRoute>} />
              <Route path="/quadrats/:quadratId/edit" element={<ProtectedRoute permission="edit_survey"><QuadratForm /></ProtectedRoute>} />

              {/* Records within a quadrat */}
              <Route path="/quadrats/:quadratId/mature-trees/new" element={<ProtectedRoute permission="create_survey"><MatureTreeForm /></ProtectedRoute>} />
              <Route path="/quadrats/:quadratId/mature-trees/:treeId/edit" element={<ProtectedRoute permission="edit_survey"><MatureTreeForm /></ProtectedRoute>} />
              <Route path="/quadrats/:quadratId/regeneration-plots/new" element={<ProtectedRoute permission="create_survey"><RegenerationPlotForm /></ProtectedRoute>} />
              <Route path="/quadrats/:quadratId/regeneration-plots/:plotId/edit" element={<ProtectedRoute permission="edit_survey"><RegenerationPlotForm /></ProtectedRoute>} />
              <Route path="/quadrats/:quadratId/disturbances/new" element={<ProtectedRoute permission="create_survey"><DisturbanceForm /></ProtectedRoute>} />
              <Route path="/quadrats/:quadratId/disturbances/:disturbanceId/edit" element={<ProtectedRoute permission="edit_survey"><DisturbanceForm /></ProtectedRoute>} />

              {/* Administration */}
              <Route path="/species" element={<ProtectedRoute permission="view_survey"><SpeciesPage /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute role="admin"><UsersPage /></ProtectedRoute>} />
              <Route path="/help" element={<ProtectedRoute><UserGuide /></ProtectedRoute>} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </Box>
      {user && <Footer />}
      <UpdatePrompt />
    </Box>
  );
};

const App = () => (
  <NetworkProvider>
    <AuthProvider>
      <SyncProvider>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </SyncProvider>
    </AuthProvider>
  </NetworkProvider>
);

export default App;
